/**
 * Organizations Service
 *
 * Service for handling organization management API calls including
 * CRUD operations for organizations.
 */

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';

// API base URL from environment variable
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:14102/api/v1';

// Token storage keys (shared with authService)
const ACCESS_TOKEN_KEY = 'access_token';

/**
 * Organization interface
 */
export interface Organization {
  id: number;
  name: string;
  slug: string;
  status: 'active' | 'inactive' | 'suspended';
  created_at: string;
  updated_at: string;
}

/**
 * Pagination interface
 */
export interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

/**
 * Paginated response interface
 */
export interface PaginatedResponse<T> {
  data: T[];
  pagination: Pagination;
}

/**
 * Create organization DTO
 */
export interface CreateOrganizationDto {
  name: string;
  slug: string;
}

/**
 * Update organization DTO
 */
export interface UpdateOrganizationDto {
  name?: string;
  slug?: string;
  status?: 'active' | 'inactive' | 'suspended';
}

/**
 * In-memory token storage
 * Initialize from localStorage to persist across page refreshes
 */
let accessToken: string | null = localStorage.getItem(ACCESS_TOKEN_KEY);

/**
 * Create Axios instance with auth interceptors
 */
const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: true,
  });

  // Request interceptor - add access token to requests
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = accessToken || localStorage.getItem(ACCESS_TOKEN_KEY);
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
        // Sync in-memory token if it was retrieved from localStorage
        if (!accessToken && token) {
          accessToken = token;
        }
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor - handle 401 errors by re-syncing token from localStorage
  // Token refresh is handled by authService's interceptor; this avoids race conditions
  // from multiple independent refresh attempts consuming the rotating refresh token.
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        // Re-read token from localStorage (authService may have refreshed it)
        const currentToken = localStorage.getItem(ACCESS_TOKEN_KEY);
        if (currentToken && currentToken !== accessToken) {
          accessToken = currentToken;
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${currentToken}`;
          }
          return client(originalRequest);
        }
      }
      return Promise.reject(error);
    }
  );

  return client;
};

// Create the API client instance
const apiClient = createApiClient();

/**
 * Organizations Service
 * Provides organization management API methods
 */
export const organizationsService = {
  /**
   * List all organizations with pagination
   * @param page - Page number (default: 1)
   * @param pageSize - Number of items per page (default: 20)
   */
  async getOrganizations(page?: number, pageSize?: number): Promise<PaginatedResponse<Organization>> {
    const response = await apiClient.get('/organizations', {
      params: { page, limit: pageSize },
    });
    const raw = response.data;
    // Map backend response format to frontend PaginatedResponse
    return {
      data: raw.data,
      pagination: {
        page: raw.page || 1,
        limit: raw.pageSize || 20,
        total: raw.total || 0,
        pages: raw.totalPages || 1,
      },
    };
  },

  /**
   * Get a single organization by ID
   */
  async getOrganization(id: number): Promise<Organization> {
    const response = await apiClient.get<Organization>(`/organizations/${id}`);
    return response.data;
  },

  /**
   * Create a new organization
   */
  async createOrganization(data: CreateOrganizationDto): Promise<Organization> {
    const response = await apiClient.post<Organization>('/organizations', data);
    return response.data;
  },

  /**
   * Update an existing organization
   */
  async updateOrganization(id: number, data: UpdateOrganizationDto): Promise<Organization> {
    const response = await apiClient.patch<Organization>(`/organizations/${id}`, data);
    return response.data;
  },

  /**
   * Delete an organization
   */
  async deleteOrganization(id: number): Promise<void> {
    await apiClient.delete(`/organizations/${id}`);
  },

  /**
   * Set access token (for synchronization with authService)
   */
  setAccessToken(token: string): void {
    accessToken = token;
  },

  /**
   * Clear access token
   */
  clearAccessToken(): void {
    accessToken = null;
  },
};

export default organizationsService;
